import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  SAMLAuthProvider,
  signInWithPopup,
  signOut,
} from 'firebase/auth';
import { env } from '../../../env';
import { AsyncThunkConfig } from '../../async-thunk-config';
import {
  AdminConsoleLoginError,
  AdminConsoleState,
  AdminConsoleUser,
} from './admin-console-types';

import { ADMIN_PERSIST_STORE_KEY } from '../../../utils/constants';
import { belongsToGroup, toSamlGroups } from './admin-console-roles';

const allowedGroup = env.adminConsoleMemberOfGroup;

const getInitialAdminUser = (): AdminConsoleUser | null => {
  if (typeof window !== 'undefined' && window.sessionStorage) {
    try {
      const stored = window.sessionStorage.getItem(ADMIN_PERSIST_STORE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.token) {
          return parsed;
        }
      }
    } catch (e) {
      console.error('Failed to parse admin user from sessionStorage:', e);
    }
  }
  return null;
};

const initialState: AdminConsoleState = {
  user: getInitialAdminUser(),
  loginStatus: 'idle',
};

const provider = new SAMLAuthProvider(env.firebaseProvider);
export const adminConsoleLogin = createAsyncThunk<
  void,
  void,
  AsyncThunkConfig<AdminConsoleLoginError>
>('admin-console-login', async (_, thunkApi) => {
  try {
    const config = {
      apiKey: env.firebaseApiKey,
      authDomain: env.firebaseAuthDomain,
    };

    const auth = getAuth(initializeApp(config));

    const userCred = await signInWithPopup(auth, provider);
    const credential = await userCred?.user?.getIdTokenResult();

    const attributes = (credential?.claims?.firebase as any)
      ?.sign_in_attributes;

    if (!userCred?.user) {
      return thunkApi.rejectWithValue('GENERIC_ERROR');
    }

    const userGroups = toSamlGroups(attributes?.memberOf ?? attributes?.group);
    if (!belongsToGroup(userGroups, allowedGroup)) {
      // l'utente SAML non è abilitato: chiudo la sessione Firebase appena aperta
      await signOut(auth).catch(() => undefined);
      return thunkApi.rejectWithValue('NOT_AUTHORIZED');
    }

    const adminUser: AdminConsoleUser = {
      token: credential.token,
      name: attributes?.NOME,
      surname: attributes?.COGNOME,
      groups: userGroups,
    };

    thunkApi.dispatch(adminConsoleSliceActions.setUser(adminUser));

    if (typeof window !== 'undefined' && window.sessionStorage) {
      try {
        window.sessionStorage.setItem(
          ADMIN_PERSIST_STORE_KEY,
          JSON.stringify(adminUser)
        );
      } catch (e) {
        console.error('Failed to save admin user to sessionStorage:', e);
      }
    }
    return null;
  } catch (error) {
    console.error('Login error:', error);
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});

const adminConsoleSlice = createSlice({
  name: 'features/adminConsole/common',
  initialState,
  reducers: {
    setUser(state, action: PayloadAction<AdminConsoleUser>) {
      state.user = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(adminConsoleLogin.pending, (state) => {
        state.loginStatus = 'loading';
        state.loginError = undefined;
      })
      .addCase(adminConsoleLogin.fulfilled, (state) => {
        state.loginStatus = 'succeeded';
      })
      .addCase(adminConsoleLogin.rejected, (state, action) => {
        state.loginStatus = 'failed';
        state.loginError = action.payload ?? 'GENERIC_ERROR';
      });
  },
});

export const adminConsoleSliceActions = adminConsoleSlice.actions;

export default adminConsoleSlice.reducer;
